import React from 'react';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { Alert, Box, Button, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { joinURL, win } from '../../../../../utilities';
import {
  citizenAreaPathName,
  diaryNoteCreate,
  diaryNotePathName,
  diaryTabPathName,
  serviceProviderAreaPathName,
} from '../../citizen-serviceprovider-area-pathnames';
import { EventStatusType } from '../../../../../api-service/clients';
import { FormDialog } from '../../../../../components/form-dialog';
import type {
  ManageServiceProviderEventDialogEvent,
  UpdateServiceProviderEventValues,
} from './event-dialog-types';
import { ServiceProviderEventFormFields } from '../service-provider-event-form-fields';
import { useServiceProviderEventFormState } from './use-service-provider-event-form-state';
import { RecurrenceEditor } from '../../../../../components/calendar/recurrence/recurrence-editor';
import type { RecurrencePattern } from '../../../../../components/calendar/recurrence/recurrence-pattern';

const FUTURE_EVENT_FOLLOWUP_TOOLTIP =
  'Kan ikke oprette opfølgning på en fremtidig begivenhed';

/** Partial-objective actions, present only when the event has both a partialObjectiveId and a resolvable citizenId. */
interface PartialObjectiveActions {
  onCreateFollowup: () => void;
  /** Whether "Opret opfølgning" is enabled — disabled for future-dated events. */
  canCreateFollowup: boolean;
  onGoToPartialObjective: () => void;
}

interface EditEventDialogProps {
  event: ManageServiceProviderEventDialogEvent;
  onClose: () => void;
  onCancel: () => void;
  onDone: () => void;
  onUnmark: (eventId: string) => Promise<void>;
  onDelete: () => void;
  onSubmit: (event: UpdateServiceProviderEventValues) => Promise<void>;
  /** When set, renders the "Delmål" menu. Only provided when the event has a partialObjectiveId and a citizenId. */
  partialObjectiveActions?: PartialObjectiveActions;
}

export function EditEventDialog(props: EditEventDialogProps) {
  const { event, onClose, onCancel, onDone, onUnmark, onDelete, onSubmit, partialObjectiveActions } = props;
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [recurrence, setRecurrence] = React.useState<RecurrencePattern | undefined>(event.recurrence);
  const [partialObjectiveMenuAnchor, setPartialObjectiveMenuAnchor] =
    React.useState<HTMLElement | null>(null);
  const isEventDone = event.eventStatus === EventStatusType.Done;
  const isEventCancelled = event.eventStatus === EventStatusType.Cancelled;
  const canUnmark = isEventDone || isEventCancelled;
  const {
    formState,
    validationErrors,
    setField,
    onAllDayChange,
    onStartDateChange,
    setStartDateValidationError,
    setStartTimeValidationError,
    setEndDateValidationError,
    setEndTimeValidationError,
    buildSubmitValues,
  } = useServiceProviderEventFormState({
    title: event.title ?? '',
    locationAddress: event.locationAddress ?? '',
    responsibleUserId: event.responsibleUserId,
    allDay: event.allDay,
    startAt: event.startAt,
    endAt: event.endAt,
    content: event.content ?? '',
    noteTypeId: event.noteTypeId,
  });

  function handleClose() {
    if (isSubmitting) {
      return;
    }

    onClose();
  }

  async function handleSubmit() {
    const submitValuesResult = buildSubmitValues();
    if (!submitValuesResult.values) {
      setError(submitValuesResult.error ?? null);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        eventId: event.eventId,
        ...submitValuesResult.values,
        consumedMinutes: event.consumedMinutes,
        recurrence,
      });
      onClose();
    } catch {
      setError('Kunne ikke opdatere begivenhed. Prøv igen.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const canCreateDiaryNote = !!event.citizenId && !!event.serviceProviderUnitId;

  function handleOpenDiaryNote() {
    if (!event.citizenId || !event.serviceProviderUnitId) {
      return;
    }

    const baseUrl = joinURL(
      citizenAreaPathName,
      serviceProviderAreaPathName,
      event.citizenId,
      diaryTabPathName,
      diaryNotePathName,
      diaryNoteCreate
    );
    const params = new URLSearchParams({ spuid: event.serviceProviderUnitId });
    if (formState.noteTypeId) {
      params.set('contenttypeid', formState.noteTypeId);
    }
    win.open(`${baseUrl}?${params.toString()}`, '_blank');
  }

  async function handleUnmark() {
    setIsSubmitting(true);
    setError(null);
    try {
      await onUnmark(event.eventId);
      onClose();
    } catch {
      setError('Kunne ikke fjerne markering fra begivenheden. Prøv igen.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <FormDialog
      open
      title="Begivenhed"
      disableBackdropClick
      confirmButtonText="Gem"
      cancelButtonText="Annuller"
      onClose={handleClose}
      onCancelClick={handleClose}
      onConfirmClick={handleSubmit}
      isLoading={isSubmitting}
      fullWidth
      maxWidth="sm"
      sx={{ '& .MuiDialogActions-root': { px: 3 } }}
      extraDialogAction={
        <Stack direction="row" spacing={1} sx={{ mr: 'auto' }}>
          <Button
            variant="outlined"
            color="error"
            onClick={onDelete}
            disabled={!event.canDelete || isSubmitting}
            startIcon={<DeleteOutlineOutlinedIcon />}
          >
            Slet
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleOpenDiaryNote}
            disabled={!canCreateDiaryNote || isSubmitting}
            startIcon={<NoteAddOutlinedIcon />}
          >
            Opret dagbogsnotat
          </Button>
          {partialObjectiveActions && (
            <>
              <Button
                variant="outlined"
                color="primary"
                onClick={menuEvent => setPartialObjectiveMenuAnchor(menuEvent.currentTarget)}
                disabled={isSubmitting}
                endIcon={<ArrowDropDownIcon />}
              >
                Delmål
              </Button>
              <Menu
                anchorEl={partialObjectiveMenuAnchor}
                open={Boolean(partialObjectiveMenuAnchor)}
                onClose={() => setPartialObjectiveMenuAnchor(null)}
              >
                <Tooltip
                  title={FUTURE_EVENT_FOLLOWUP_TOOLTIP}
                  disableHoverListener={partialObjectiveActions.canCreateFollowup}
                >
                  <span>
                    <MenuItem
                      disabled={!partialObjectiveActions.canCreateFollowup}
                      onClick={() => {
                        setPartialObjectiveMenuAnchor(null);
                        partialObjectiveActions.onCreateFollowup();
                      }}
                    >
                      Opret opfølgning
                    </MenuItem>
                  </span>
                </Tooltip>
                <MenuItem
                  onClick={() => {
                    setPartialObjectiveMenuAnchor(null);
                    partialObjectiveActions.onGoToPartialObjective();
                  }}
                >
                  Gå til delmål
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>
      }
      dontCapitalizeButtonTexts
    >
      {(event.citizenName || event.citizenCpr) && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {event.citizenName}
          {event.citizenName && event.citizenCpr ? ' · ' : ''}
          {event.citizenCpr}
        </Typography>
      )}
      {event.observationTypeName && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Observationstype: {event.observationTypeName}
        </Typography>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }}>
        <Tooltip
          title="Begivenhed er allerede aflyst"
          disableHoverListener={!isEventCancelled}
        >
          <span style={{ display: 'flex', flex: 1 }}>
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={!event.canCancel || isEventCancelled || isSubmitting}
              sx={{ flex: 1 }}
              startIcon={<EventBusyOutlinedIcon />}
            >
              Aflys
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title="Begivenhed er allerede udført"
          disableHoverListener={!isEventDone}
        >
          <span style={{ display: 'flex', flex: 1 }}>
            <Button
              variant="outlined"
              onClick={onDone}
              disabled={isEventDone || isSubmitting}
              sx={{ flex: 1 }}
              startIcon={<TaskAltOutlinedIcon />}
            >
              Markér som udført
            </Button>
          </span>
        </Tooltip>
        {canUnmark && (
          <Button
            variant="outlined"
            color="warning"
            onClick={handleUnmark}
            disabled={isSubmitting}
            sx={{ flex: 1 }}
          >
            Fjern status
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <ServiceProviderEventFormFields
        errors={validationErrors}
        title={formState.title}
        onTitleChange={value => setField('title', value)}
        locationAddress={formState.locationAddress}
        onLocationAddressChange={value => setField('locationAddress', value)}
        responsibleUserId={formState.responsibleUserId}
        onResponsibleUserIdChange={value => setField('responsibleUserId', value)}
        allDay={formState.allDay}
        onAllDayChange={onAllDayChange}
        startDate={formState.startDate}
        onStartDateChange={onStartDateChange}
        onStartDateValidation={setStartDateValidationError}
        startTime={formState.startTime}
        onStartTimeChange={value => setField('startTime', value)}
        onStartTimeValidation={setStartTimeValidationError}
        endDate={formState.endDate}
        onEndDateChange={value => setField('endDate', value)}
        onEndDateValidation={setEndDateValidationError}
        endTime={formState.endTime}
        onEndTimeChange={value => setField('endTime', value)}
        onEndTimeValidation={setEndTimeValidationError}
        content={formState.content}
        onContentChange={value => setField('content', value)}
        noteTypeId={formState.noteTypeId}
        onNoteTypeIdChange={value => setField('noteTypeId', value)}
      />
      <Box sx={{ mt: 2 }}>
        <RecurrenceEditor value={recurrence} onChange={setRecurrence} />
      </Box>
    </FormDialog>
  );
}
